/**
 * Optional listener profile sections (Sam, Oct 18 2026): a one-line quote,
 * up to 3 personality words and "What a conversation with me feels like".
 * Stored in listing publicData as profileQuote / personalityWords /
 * conversationFeel. Empty fields are saved as null and show nothing.
 * Free text goes through the existing profile moderation (server picks up
 * every string in listing publicData).
 */

export const PROFILE_QUOTE_MAX = 90;
export const CONVERSATION_FEEL_MAX = 200;
export const PERSONALITY_WORDS_MAX = 3;

export const PERSONALITY_WORDS = [
  { id: 'friendly', label: 'Friendly' },
  { id: 'down-to-earth', label: 'Down-to-earth' },
  { id: 'calm', label: 'Calm' },
  { id: 'upbeat', label: 'Upbeat' },
  { id: 'funny', label: 'Funny' },
  { id: 'thoughtful', label: 'Thoughtful' },
  { id: 'patient', label: 'Patient' },
  { id: 'easygoing', label: 'Easygoing' },
  { id: 'warm', label: 'Warm' },
  { id: 'honest', label: 'Honest' },
  { id: 'encouraging', label: 'Encouraging' },
  { id: 'good-listener', label: 'Good listener' },
];

const WORD_IDS = PERSONALITY_WORDS.map(w => w.id);
const LABEL_BY_ID = PERSONALITY_WORDS.reduce((acc, w) => ({ ...acc, [w.id]: w.label }), {});
const ID_BY_LABEL = PERSONALITY_WORDS.reduce(
  (acc, w) => ({ ...acc, [w.label.toLowerCase()]: w.id }),
  {}
);

// Strip quote marks people type around their own quote; we add our own.
const QUOTE_EDGES = /^[\s"'“”‘’«»]+|[\s"'“”‘’«»]+$/g;

const oneLine = s => s.replace(/\s+/g, ' ');

/**
 * @param {*} value
 * @returns {string}
 */
export const normalizeProfileQuote = value => {
  if (typeof value !== 'string') {
    return '';
  }
  return oneLine(value)
    .replace(QUOTE_EDGES, '')
    .slice(0, PROFILE_QUOTE_MAX)
    .trim();
};

/**
 * @param {*} value
 * @returns {string}
 */
export const normalizeConversationFeel = value => {
  if (typeof value !== 'string') {
    return '';
  }
  return value
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, CONVERSATION_FEEL_MAX)
    .trim();
};

/**
 * Preset ids only, max 3, in the order picked. Accepts labels too.
 *
 * @param {*} value
 * @returns {Array<string>}
 */
export const normalizePersonalityWords = value => {
  const list = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const out = [];
  list.forEach(item => {
    const raw = String(item || '').trim();
    const id = WORD_IDS.includes(raw) ? raw : ID_BY_LABEL[raw.toLowerCase()];
    if (id && !out.includes(id) && out.length < PERSONALITY_WORDS_MAX) {
      out.push(id);
    }
  });
  return out;
};

export const personalityWordLabel = id => LABEL_BY_ID[id] || '';

/**
 * "Friendly · Down-to-earth · Calm"
 *
 * @param {*} words
 * @returns {string}
 */
export const personalityLine = words =>
  normalizePersonalityWords(words)
    .map(personalityWordLabel)
    .join(' · ');

/**
 * Form initial values from listing publicData.
 *
 * @param {Object} [publicData]
 */
export const profileSectionsFromPublicData = (publicData = {}) => ({
  profileQuote: normalizeProfileQuote(publicData?.profileQuote),
  personalityWords: normalizePersonalityWords(publicData?.personalityWords),
  conversationFeel: normalizeConversationFeel(publicData?.conversationFeel),
});

/**
 * publicData patch for save. null clears a field in Sharetribe extended data.
 *
 * @param {Object} values
 */
export const profileSectionsForSave = (values = {}) => {
  const quote = normalizeProfileQuote(values.profileQuote);
  const words = normalizePersonalityWords(values.personalityWords);
  const feel = normalizeConversationFeel(values.conversationFeel);
  return {
    profileQuote: quote || null,
    personalityWords: words.length ? words : null,
    conversationFeel: feel || null,
  };
};
